import React, { useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getActiveProduct } from '../../../store/products/selectors';
import { setCookie } from '../../../shared/utils';
import localization from '../../../shared/strings';
import {
  selectSelectedLiveProduct,
  selectLiveVariantForDisplay,
  selectSelectedListingVariants,
  selectEffectiveChannelState,
  selectMasterProduct,
} from '../../../store/selectors/productSelectors';
import { getDetailsPanelVisibility } from '../../details/helpers/getDetailsPanelDefaultState';
import RelativeTime from '../../RelativeTime';
import Description from '../Description';
import Inventory from '../Inventory/Inventory';
import Options from '../Options/Options';
import Pricing from '../Pricing';
import picsioConfig from '../../../../../../config';
import ProductStatusSelect from '../ProductStatusSelect';
import ProductType from '../ProductType';
import Category from '../Category';
import Shipping from '../Shipping';
import VariantDetailsHeader from '../VariantDetailsHeader';
import VariantContextBlock from '../VariantContextBlock';
import { navigateToVariantParent } from '../helpers/navigateToVariantParent';
import { getVariantUnitCost } from '../helpers/getVariantUnitCost';
import DetailsBlock from '../../DetailsBlock';
import { INVENTORY_PRESETS, SHIPPING_PRESETS } from '../helpers/productDetailsPresets';
import ProductName from '../ProductName';
import Brand from '../Vendor';
import Keywords from '../Keywords';
import SeoSection from '../SeoSection';
import { stripHtmlToPlainText } from '../SeoSection/helpers/stripHtmlToPlainText';
import ProductMetaFields from '../ProductMetafields';
import styles from '../styles.module.scss';

const l10n = localization.PRODUCT_INFO;
const publishedReadOnlyTooltip = l10n.publishedReadOnlyFieldTooltip;
const masterPreviewTooltip = l10n.variantNotOnChannelPreviewTooltip;

const LiveTab = () => {
  const location = useLocation();
  const { productId, variantId } = useMemo(() => getActiveProduct(location), [location]);
  const product = useSelector((state) => selectSelectedLiveProduct(state, productId));
  const masterProduct = useSelector((state) => selectMasterProduct(state, productId));
  const listingVariantsOnChannel = useSelector((state) => selectSelectedListingVariants(state, productId));
  const liveVariant = useSelector((state) => selectLiveVariantForDisplay(state, productId, variantId));
  const { contextChannelId } = useSelector((state) => selectEffectiveChannelState(state, productId, variantId));
  const masterVariant = useMemo(
    () => (variantId ? masterProduct.variants?.find(({ _id }) => _id === variantId) ?? null : null),
    [masterProduct.variants, variantId],
  );
  const variant = variantId ? (liveVariant ?? masterVariant) : null;
  const isMasterPreview = Boolean(variantId && !liveVariant && masterVariant);
  const showParentCommerceFields = listingVariantsOnChannel.length === 0;
  const variantReadOnlyTooltip = isMasterPreview ? masterPreviewTooltip : publishedReadOnlyTooltip;

  const [panelsVisibility, setPanelsVisibility] = useState(getDetailsPanelVisibility());

  const toggleVisibility = useCallback((title) => {
    const detailsPanelVisibility = { ...panelsVisibility };

    detailsPanelVisibility[title] = !detailsPanelVisibility[title];
    setCookie('picsio.detailsPanelVisibility', JSON.stringify(detailsPanelVisibility));
    setPanelsVisibility(detailsPanelVisibility);
  }, [panelsVisibility]);

  const onNavigateToListingParent = useCallback(() => {
    navigateToVariantParent({ productId });
  }, [productId]);

  return (
    <>
      <If condition={!variant}>
        <div className={styles.statusSelect}>
          <ProductStatusSelect
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            productStatus={product.status}
          />
        </div>
      </If>
      <If condition={!variant}>
        <DetailsBlock
          dataQa="productCore"
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          titleActions={[
            <RelativeTime key="lastPublished" value={product.updatedAt}>
              {(time) => (
                <span className={styles.lastPublished}>
                  {localization.PRODUCT_INFO.lastPublished(time)}
                </span>
              )}
            </RelativeTime>,
          ]}
          blockName="productCoreVisibility"
          blockTitle="Core"
        >
          <ProductName value={product?.title || ''} readOnly readOnlyTooltip={publishedReadOnlyTooltip} />
          <Brand value={product?.vendor || ''} readOnly readOnlyTooltip={publishedReadOnlyTooltip} />
          <ProductType value={product?.productType || ''} readOnly readOnlyTooltip={publishedReadOnlyTooltip} />
          <If condition={picsioConfig.isPim}>
            <Category value={product?.category || null} readOnly readOnlyTooltip={publishedReadOnlyTooltip} />
          </If>
          <Description
            title="Description"
            additionalConfig={{ readonly: true }}
            currentValue={product?.descriptionHtml || ''}
            readOnlyTooltip={publishedReadOnlyTooltip}
          />
          <Keywords
            productId={productId}
            keywords={product?.keywords || []}
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
          />
        </DetailsBlock>
        <br />
        <SeoSection
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productSeoVisibility"
          blockTitle={l10n.seo.sectionTitle}
          handle={product?.handle || ''}
          seoTitle={product?.seoTitle || ''}
          seoDescription={product?.seoDescription || ''}
          fallbackTitle={product?.title || ''}
          fallbackDescription={stripHtmlToPlainText(product?.descriptionHtml || product?.description || '')}
          readOnly
          readOnlyTooltip={publishedReadOnlyTooltip}
        />
        <br />
        <Options
          readOnly
          showDisabledManagementActions
          disabledManagementTooltip={publishedReadOnlyTooltip}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productOptionsVisibility"
          blockTitle={l10n.variantStructure}
          currentValue={product?.availableOptions || []}
        />
        <If condition={showParentCommerceFields}>
          <br />
          <Pricing
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            price={product?.price || ''}
            compareAtPrice={product?.compareAtPrice || ''}
            taxable={product?.taxable || false}
            unitCost={getVariantUnitCost(product)}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productPricingVisibility"
            blockTitle="Pricing"
            onChangePrice={() => null}
            onChangeCompareAtPrice={() => null}
            onChangeTaxable={() => null}
            onChangeUnitCost={() => null}
          />
          <br />
          <Inventory
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            {...INVENTORY_PRESETS.skuCodes}
            sku={product?.sku || ''}
            barcode={product?.barcode || ''}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productInventoryVisibility"
            blockTitle={l10n.skuCodesBlockTitle}
            onChangeSku={() => null}
            onChangeBarcode={() => null}
            onChangeTracked={() => null}
          />
          <br />
          <Shipping
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            {...SHIPPING_PRESETS.simple}
            currentValue={product?.weight || { value: 0, unit: 'grams' }}
            requiresShipping={product?.requiresShipping || false}
            countryCodeOfOrigin={product?.countryCodeOfOrigin || ''}
            harmonizedSystemCode={product?.harmonizedSystemCode || ''}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productShippingVisibility"
            blockTitle={l10n.shippingCompliance}
            onChangeRequiresShipping={() => null}
            onChangeWeight={() => null}
            onChangeCountryCodeOfOrigin={() => null}
            onChangeHarmonizedSystemCode={() => null}
          />
        </If>
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            meta={product?.meta}
            layer="listing"
            channelId={contextChannelId}
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            attributeScope="product"
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productMetaVisibility"
          />
          <br />
        </If>
      </If>

      <If condition={variant}>
        <VariantDetailsHeader
          productTitle={product?.title || ''}
          variantDisplayName={variant?.displayName || variant?.title || ''}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
        />
        <VariantContextBlock
          parentTitle={product?.title || ''}
          parentSku={product?.sku || ''}
          parentStatus={product?.status}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
          onNavigateToParent={onNavigateToListingParent}
        />
        <br />
        <Pricing
          readOnly
          readOnlyTooltip={variantReadOnlyTooltip}
          price={variant?.price || ''}
          compareAtPrice={variant?.compareAtPrice || ''}
          taxable={variant?.taxable || false}
          unitCost={getVariantUnitCost(variant)}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productPricingVisibility"
          blockTitle="Pricing"
          onChangePrice={() => null}
          onChangeCompareAtPrice={() => null}
          onChangeTaxable={() => null}
          onChangeUnitCost={() => null}
        />
        <br />
        <Inventory
          readOnly
          readOnlyTooltip={variantReadOnlyTooltip}
          {...INVENTORY_PRESETS.skuCodes}
          tracked={variant?.tracked || false}
          sku={variant?.sku || ''}
          barcode={variant?.barcode || ''}
          skuLabel={l10n.variantSku}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productInventoryVisibility"
          blockTitle={l10n.skuCodesBlockTitle}
          onChangeSku={() => null}
          onChangeBarcode={() => null}
          onChangeTracked={() => null}
        />
        <br />
        <Shipping
          readOnly
          readOnlyTooltip={variantReadOnlyTooltip}
          {...SHIPPING_PRESETS.variant}
          currentValue={variant?.weight || { value: 0, unit: 'grams' }}
          requiresShipping={variant?.requiresShipping || false}
          countryCodeOfOrigin={variant?.countryCodeOfOrigin || ''}
          harmonizedSystemCode={variant?.harmonizedSystemCode || ''}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productShippingVisibility"
          blockTitle="Shipping"
          onChangeRequiresShipping={() => null}
          onChangeWeight={() => null}
          onChangeCountryCodeOfOrigin={() => null}
          onChangeHarmonizedSystemCode={() => null}
        />
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            variantId={variantId}
            meta={variant?.meta}
            layer="listing"
            channelId={contextChannelId}
            readOnly
            readOnlyTooltip={publishedReadOnlyTooltip}
            attributeScope="variant"
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="variantMetaVisibility"
          />
          <br />
        </If>
      </If>
    </>
  );
};

export default LiveTab;
